@if (currentStep === 1) {
  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="site-form">
    <nb-card [ngClass]="customNbCardClass" [nbSpinner]="loading">
      <nb-card-header>
        @if (site) {
          <div class="title">{{ site.name }}</div>
        } @else {
          <div class="title">{{ 'New site' | translate }}</div>
        }
        @if (canLeave) {
          <div class="cancel-button" (click)="close()">
            <i class="fal fa-x"></i>
          </div>
        }
      </nb-card-header>
      <nb-card-body>

        <div class="form-group">
          <div class="form-field">
            <label for="name">{{ 'Site label' | translate }}</label>
            <input
              nbInput
              id="name"
              type="text"
              formControlName="name"
              fullWidth
            >
          </div>

          <div class="form-field" [nbSpinner]="(clusters$ | async)?.loading">
            <label for="cluster">{{ 'Site typology' | translate }}</label>
            @if (siteId) {
              <input
                nbInput
                id="cluster"
                type="text"
                [value]="getClusterName(form.get('cluster')?.value)"
                readonly
                fullWidth
                [style.background-color]="'#f5f5f5'"
              >
            } @else {
              <nb-select
                id="cluster"
                formControlName="cluster"
                placeholder="-"
                fullWidth
                >
                @for (c of (clusters$ | async)?.data; track c) {
                  <nb-option [value]="c.id">
                    {{ c.name }}
                  </nb-option>
                }
              </nb-select>
            }
          </div>
          @if (!siteId) {
            <!-- Create mode: single picture upload -->
            <div class="form-field">
              <label for="picture">{{ 'Site photo' | translate }}</label>
              <input
                nbInput
                id="picture"
                type="file"
                accept="image/*"
                fullWidth
                (change)="onFileSelected($event)"
              >
              @if (picturePreviewUrl) {
                <div class="mt-1 d-flex justify-content-start">
                  <div class="position-relative picture-preview">
                    <img [src]="picturePreviewUrl" alt="Preview" class="preview-image">
                    <button type="button" class="delete-picture-button" (click)="deletePicture()">
                      <i class="fal fa-x"></i>
                    </button>
                  </div>
                </div>
              }
            </div>
          } @else {
            <!-- Edit mode: multi-picture management -->
            <div class="form-field" [nbSpinner]="picturesLoading">
              <label>{{ 'Site photos' | translate }}</label>

              <!-- Add new picture: preview shows inline, saved on Update -->
              <input
                id="new-picture"
                type="file"
                accept="image/*"
                style="display:none"
                (change)="onNewPictureSelected($event)"
              >
              <div class="pictures-list">
                @if (existingPictures.length > 0) {
                  @for (pic of existingPictures; track pic.id) {
                    <div class="picture-item" [class.picture-item--cover]="isCover(pic)">
                      <img [src]="pic.thumbnail || pic.picture" alt="Photo" class="preview-image">
                      <div class="picture-item__actions">
                        <button type="button" class="picture-action-btn" [disabled]="isCover(pic)" (click)="setAsCover(pic)"
                          [title]="'Set as default' | translate">
                          <i class="fal" [class.fa-star]="!isCover(pic)" [class.fa-star-solid]="isCover(pic)"></i>
                        </button>
                        <button type="button" class="picture-action-btn picture-action-btn--delete" (click)="deleteExistingPicture(pic)"
                          [title]="'Delete' | translate">
                          <i class="fal fa-trash-can"></i>
                        </button>
                      </div>
                    </div>
                  }
                }
                @for (previewUrl of newPicturePreviewUrls; track previewUrl; let i = $index) {
                  <div class="picture-item picture-item--pending">
                    <img [src]="previewUrl" alt="New photo" class="preview-image">
                    <div class="picture-item__actions">
                      <button type="button" class="picture-action-btn picture-action-btn--delete" (click)="cancelNewPicture(i)"
                        [title]="'Remove' | translate">
                        <i class="fal fa-x"></i>
                      </button>
                    </div>
                  </div>
                }
                <button type="button" class="add-photo-btn" (click)="triggerNewPictureInput()"
                  [title]="'Add a photo' | translate">
                  <i class="fa-thin fa-plus"></i>
                </button>
              </div>
            </div>
          }

          <div formArrayName="perimeters">
            @for (perimeter of getPerimetersFormArray().controls; track perimeter; let i = $index) {
              <div [formGroupName]="i">
                  <label [for]="'perimeter_name_' + i" class="perimeter-label">
                    <span class="perimeter-label-text">{{ 'Building' | translate }} n°{{ i + 1 }}</span>
                    @if (canRemovePerimeter()) {
                      <button
                        type="button"
                        class="delete-perimeter-button"
                        (click)="removePerimeter(i)"
                        [attr.aria-label]="'Delete building' | translate"
                      >
                        <i class="fal fa-trash-can"></i>
                      </button>
                    }
                  </label>

                <div class="form-field">
                  <label [for]="'perimeter_name_' + i">{{ 'Building label' | translate }}</label>
                  <input
                    nbInput
                    [id]="'perimeter_name_' + i"
                    type="text"
                    formControlName="name"
                    fullWidth
                  >
                </div>

                @if (isBuildingPerimeter(i)) {
                  <div class="form-field">
                    <label [for]="'perimeter_surface_' + i">{{ 'Surface ratio energy / carbon of the building' | translate }}</label>
                    <input
                      nbInput
                      [id]="'perimeter_surface_' + i"
                      type="number"
                      formControlName="flooring_area"
                      fullWidth
                    >
                  </div>
                }

                <div class="form-field">
                  <label [for]="'perimeter_typology_' + i">{{ 'Building typology' | translate }}</label>
                  <nb-select
                    [id]="'perimeter_typology_' + i"
                    formControlName="perimeter_type"
                    placeholder="-"
                    fullWidth
                    >
                    @for (typology of perimeterTypes; track typology) {
                      <nb-option [value]="typology.id">
                        {{ typology.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>

                <div class="form-field">
                  <label [for]="'perimeter_year_' + i">{{ 'Year of construction / renovation of the building' | translate }}</label>
                  <input
                    nbInput
                    [id]="'perimeter_year_' + i"
                    type="number"
                    min="1900"
                    [max]="currentYear"
                    formControlName="creation_year"
                    fullWidth
                  >
                </div>
              </div>
            }

              <button
                type="button"
                class="add-perimeter-button"
                (click)="addPerimeter()"
              >
                <i class="fa-thin fa-plus"></i>
                {{ 'Add a building' | translate }}
              </button>
          </div>

        </div>

      </nb-card-body>
      <!-- CANCEL OR SUBMIT -->
      @if (canAdd && !siteId) {
        <nb-card-footer class="wizard">
          <button nbButton type="submit" class="wizard-next" [disabled]="!isFormValid()">
            {{ "Next" | translate }}
          </button>
        </nb-card-footer>
      }
      <!-- Update the site/mono perimeter-->
      @if (canModify() && siteId) {
        <nb-card-footer class="wizard">
          <button nbButton type="submit" class="wizard-next" [disabled]="!isFormValid() || !anyFieldValueChanged()">
            {{ "Update" | translate }}
          </button>
        </nb-card-footer>
      }
    </nb-card>
  </form>
}

@if (currentStep === 2) {
  <nb-card [nbSpinner]="loading">
    <nb-card-header>
      <div class="title">{{ 'Users' | translate }}</div>
      <div class="cancel-button" (click)="close()">
        <i class="fal fa-x"></i>
      </div>
    </nb-card-header>
    <nb-card-body style="padding: 16px 24px;">
      <div class="form-field" style="margin-bottom: 16px;">
        <input
          nbInput
          fullWidth
          type="text"
          [(ngModel)]="userSearchText"
          [placeholder]="'Search' | translate"
        >
      </div>
      <div style="display: flex; align-items: center; padding: 8px 12px; border-bottom: 1px solid #e0e0e0;"
           [style.opacity]="hasEditPermission ? '1' : '0.6'">
        <nb-checkbox
          [checked]="areAllSelected()"
          [indeterminate]="areSomeSelected() && !areAllSelected()"
          [disabled]="!hasEditPermission"
          (checkedChange)="toggleSelectAll()"
        ></nb-checkbox>
        <span style="margin-left: 12px; font-weight: 500; font-size: 13px; cursor: pointer;" (click)="hasEditPermission && toggleSelectAll()">
          {{ (areAllSelected() ? 'Deselect all' : 'Select all') | translate }}
        </span>
      </div>
      <div style="overflow-y: auto; max-height: calc(100vh - 320px);">
        @for (user of filteredUsers; track user.id) {
          <div style="display: flex; align-items: center; padding: 10px 12px; border-bottom: 1px solid #f0f0f0;"
               [style.cursor]="(isCurrentUser(user) || !hasEditPermission) ? 'default' : 'pointer'"
               [style.opacity]="(isCurrentUser(user) || !hasEditPermission) ? '0.6' : '1'"
               (click)="toggleUserSelection(user)">
            <nb-checkbox
              [checked]="isUserSelected(user)"
              [disabled]="isCurrentUser(user) || !hasEditPermission"
              (checkedChange)="toggleUserSelection(user)"
              (click)="$event.stopPropagation()"
            ></nb-checkbox>
            <div style="display: flex; flex-direction: column; margin-left: 12px;">
              <span style="font-weight: 500; font-size: 13px;">{{ getUserDisplayName(user) }}</span>
              <span style="font-size: 11px; color: #888;">{{ user.email }}</span>
            </div>
          </div>
        }
        @if (filteredUsers.length === 0 && !loading) {
          <div style="text-align: center; padding: 20px; color: #999;">{{ 'No results' | translate }}</div>
        }
      </div>
    </nb-card-body>
    <nb-card-footer class="wizard">
      <button nbButton class="wizard-next" (click)="saveWithUsers()">
        {{ "Save" | translate }}
      </button>
    </nb-card-footer>
  </nb-card>
}
